import { useEffect, useState } from 'react';
import { Button, Checkbox, CloseButton, Group, Modal, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { calculateDensityMessages } from './calculate-density-messages';
import styles from './RecommendedFreightClassModal.module.css';
import { FREIGHT_CLASS_OPTIONS } from '../freight-browser/components/freight-form/freight-class-options';

import type { FreightCommodityDensityResult } from '../../types/freight-responses';
import type { LtlCommodity } from '../../types/ltl';

export type FreightDensityAction = 'apply' | 'apply_all' | 'close';

export type RecommendedFreightClassModalProps = {
  opened: boolean;
  onClose: () => void;
  density: number | null;
  densityUnit: string;
  commodities: LtlCommodity[];
  commodityResults: FreightCommodityDensityResult[];
  onApply: (indices: number[]) => void;
  onDensityAction?: (action: FreightDensityAction) => void;
};

const findClassLabel = (freightClass: string | null | undefined) =>
  FREIGHT_CLASS_OPTIONS.find((opt) => opt.value === freightClass)?.label ?? null;

export const RecommendedFreightClassModal = ({
  opened,
  onClose,
  density,
  commodities,
  commodityResults,
  onApply,
  onDensityAction,
}: RecommendedFreightClassModalProps) => {
  const intl = useIntl();

  const recommendedIndices = commodityResults.map((r, i) => (r.recommendedFreightClass ? i : -1)).filter((i) => i >= 0);

  const changingIndices = commodityResults
    .map((r, i) => {
      const current = commodities[i]?.freightClass || '';
      return r.recommendedFreightClass && r.recommendedFreightClass !== current ? i : -1;
    })
    .filter((i) => i >= 0);

  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(() => new Set(changingIndices));

  useEffect(() => {
    const changing = commodityResults
      .map((r, i) => {
        const current = commodities[i]?.freightClass || '';
        return r.recommendedFreightClass && r.recommendedFreightClass !== current ? i : -1;
      })
      .filter((i) => i >= 0);
    setSelectedIndices(new Set(changing));
  }, [commodityResults, commodities]);

  const toggleIndex = (idx: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const handleApplyAll = () => {
    onApply(recommendedIndices);
    onDensityAction?.('apply_all');
    onClose();
  };

  const handleApplySelection = () => {
    onApply([...selectedIndices]);
    onDensityAction?.('apply');
    onClose();
  };

  const handleClose = () => {
    onDensityAction?.('close');
    onClose();
  };

  const hasAnyRecommendation = recommendedIndices.length > 0;

  return (
    <Modal centered onClose={handleClose} opened={opened} size='md' withCloseButton={false}>
      <Stack className={styles.body} gap='md'>
        <Group justify='space-between'>
          <Text fw={600}>{intl.formatMessage(calculateDensityMessages.densityModalTitle)}</Text>
          <CloseButton data-testid='recommended-class-modal-close' onClick={handleClose} />
        </Group>

        {density === null ? (
          <Text size='sm'>{intl.formatMessage(calculateDensityMessages.densityModalNoDensity)}</Text>
        ) : (
          <Stack gap='xs'>
            <Text size='sm'>{intl.formatMessage(calculateDensityMessages.densityModalDensity, { density: density.toFixed(1) })}</Text>
            <Stack gap='sm'>
              {commodityResults.map((result, idx) => {
                const commodity = commodities[idx];
                const commodityName =
                  commodity?.productName ||
                  commodity?.description ||
                  intl.formatMessage(calculateDensityMessages.commodityTitle, { number: idx + 1 });
                const currentClass = commodity?.freightClass || '';
                const currentLabel = findClassLabel(currentClass);
                const newLabel = findClassLabel(result.recommendedFreightClass);
                const hasRecommendation = result.recommendedFreightClass !== null;
                const isNoChange = hasRecommendation && result.recommendedFreightClass === currentClass;
                const isDisabled = !hasRecommendation || isNoChange;

                const emptyClassLabel = intl.formatMessage(calculateDensityMessages.densityModalEmptyClass);

                return (
                  <Group key={idx} align='center' gap='sm' wrap='nowrap'>
                    <Checkbox checked={selectedIndices.has(idx)} disabled={isDisabled} onChange={() => toggleIndex(idx)} />
                    <Text fw={500} size='sm'>
                      {commodityName}
                    </Text>
                    {hasRecommendation ? (
                      <Group gap='xs'>
                        <Text c='dimmed' size='sm'>
                          {currentLabel ?? emptyClassLabel}
                        </Text>
                        <Text c='dimmed' size='sm'>
                          →
                        </Text>
                        <Text size='sm'>{newLabel}</Text>
                        {isNoChange && (
                          <Text c='dimmed' size='sm'>
                            ({intl.formatMessage(calculateDensityMessages.densityModalNoChange)})
                          </Text>
                        )}
                      </Group>
                    ) : (
                      <Text c='dimmed' size='sm'>
                        {intl.formatMessage(calculateDensityMessages.densityModalNoRecommendation)}
                      </Text>
                    )}
                  </Group>
                );
              })}
            </Stack>
          </Stack>
        )}

        <Text c='dimmed' size='xs'>
          {intl.formatMessage(calculateDensityMessages.densityModalDisclaimer)}
        </Text>

        <Group justify='flex-end'>
          <Button onClick={handleClose} variant='tertiary'>
            {intl.formatMessage(calculateDensityMessages.densityModalClose)}
          </Button>
          <Button
            data-testid='recommended-class-modal-apply-selection'
            disabled={selectedIndices.size === 0}
            onClick={handleApplySelection}
            variant='secondary'
          >
            {intl.formatMessage(calculateDensityMessages.densityModalApplySelection)}
          </Button>
          <Button data-testid='recommended-class-modal-apply' disabled={!hasAnyRecommendation} onClick={handleApplyAll}>
            {intl.formatMessage(calculateDensityMessages.densityModalApply)}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
